/**
 * Global Error Handler
 * SECURITY: Never expose stack traces or sensitive info to users
 */

import { track } from "./track";

export interface AppError {
  message: string;
  code?: string;
  timestamp: number;
  userMessage: string;
}

/**
 * Mask sensitive data in strings
 */
export function maskSensitiveData(data: string): string {
  // Mask emails
  const emailRegex = /([a-zA-Z0-9._-]+)@([a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi;
  data = data.replace(emailRegex, (match) => {
    const [local, domain] = match.split("@");
    return `${local[0]}***@***.${domain.split(".").pop()}`;
  });

  // Mask phone numbers
  const phoneRegex = /(\+?\d{2,3})?[\s.-]?\(?\d{2,3}\)?[\s.-]?\d{4,5}[\s.-]?\d{4}/g;
  data = data.replace(phoneRegex, "***-***-****");

  // Mask CPF/CNPJ
  const cpfRegex = /\d{3}\.\d{3}\.\d{3}-\d{2}/g;
  data = data.replace(cpfRegex, "***.***.**-**");

  return data;
}

/**
 * Create user-friendly error message (never expose technical details)
 */
export function createUserMessage(error: unknown): string {
  if (error instanceof Error) {
    // NOT NULL violation (code 23502)
    if (error.message.includes("23502") || error.message.includes("null value")) {
      const match = error.message.match(/column "(\w+)"/);
      const column = match ? match[1] : "campo obrigatório";
      return `O campo "${column}" é obrigatório e não foi preenchido.`;
    }
    
    // Map common errors to user-friendly messages
    if (error.message.includes("network") || error.message.includes("fetch")) {
      return "Não foi possível conectar ao servidor. Verifique sua conexão.";
    }
    if (error.message.includes("timeout")) {
      return "A operação demorou muito tempo. Tente novamente.";
    }
    if (error.message.includes("unauthorized") || error.message.includes("401")) {
      return "Sua sessão expirou. Faça login novamente.";
    }
    if (error.message.includes("forbidden") || error.message.includes("403")) {
      return "Você não tem permissão para esta operação.";
    }
    if (error.message.includes("not found") || error.message.includes("404")) {
      return "Recurso não encontrado.";
    }
    // RLS permission denied (code 42501)
    if (error.message.includes("42501") || error.message.includes("row-level security")) {
      return "Você não tem permissão para acessar este recurso.";
    }
    // Duplicate key violation (code 23505)
    if (error.message.includes("23505") || error.message.includes("duplicate key")) {
      return "Este registro já existe no sistema.";
    }
  }

  // Generic fallback
  return "Não foi possível concluir a operação. Tente novamente.";
}

/**
 * Log error securely (no PII)
 */
export function logError(error: unknown, context?: string): AppError {
  const appError: AppError = {
    message: error instanceof Error ? error.message : String(error),
    code: error instanceof Error && "code" in error ? String(error.code) : undefined,
    timestamp: Date.now(),
    userMessage: createUserMessage(error),
  };

  // Only log full details in development
  if (import.meta.env.DEV) {
    console.error(`[Error${context ? ` - ${context}` : ""}]:`, error);
  } else {
    // In production, log sanitized version
    console.error(`[Error${context ? ` - ${context}` : ""}]:`, {
      message: maskSensitiveData(appError.message),
      code: appError.code,
      timestamp: appError.timestamp,
    });
  }

  // Track error event
  track("error_occurred" as any, {
    code: appError.code,
    context: context || "unknown",
    timestamp: appError.timestamp,
  });

  return appError;
}

/**
 * Global error handler for window errors
 */
export function setupGlobalErrorHandler(): void {
  // Catch unhandled errors
  window.addEventListener("error", (event) => {
    event.preventDefault();
    logError(event.error, "unhandled");
  });

  // Catch unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    event.preventDefault();
    logError(event.reason, "unhandled_promise");
  });

  // Log when leaving due to error
  window.addEventListener("beforeunload", () => {
    // Cleanup or final logging if needed
  });
}
